import { Capacitor } from '@capacitor/core';

import { getLatestVersion, getMinVersion, getSessionEpoch } from './appConfig';
import { getAppVersion, isBelowMinVersion, isNewerVersion, readSeenEpoch, rememberEpoch, shouldForceLogout } from './appVersion';
import { isStoreBuild } from './platform';

/**
 * Что делать при запуске и при возврате в приложение: пустить, предложить обновиться (мягкая плашка), потребовать
 * обновиться (жёсткий экран) или выйти и войти заново.
 *
 * ⚠️ ЗАЧЕМ ЭТО ЕСТЬ. Главное правило совместимости — сервер обязан понимать
 * старые приложения. Но 01.09.2026 его нарушили, и обновившиеся остались без
 * входа. Здесь страховка на случай, когда совместимость всё-таки сломана:
 * закрыть дыру можно с сервера, не дожидаясь недели проверки в App Store.
 *
 * Мягкая плашка «Вышла новая версия» (15.10.2026, решение владельца вместо OTA) — по `latestVersion`; жёсткий экран
 * остаётся по `minVersion` и важнее плашки.
 */
export type StartupDecision = 'ok' | 'update-available' | 'update-required';

/**
 * Решение по версиям — чистая функция, без платформы и сети.
 *
 * - сайт — всегда `ok`: там всегда свежая страница;
 * - ниже `min` — `update-required` (жёсткий экран);
 * - ниже `latest` — `update-available` (плашка);
 * - пусто или мусор — `ok`: ошибка в настройке не должна ни запирать, ни дёргать учеников.
 */
export function decideUpdate(input: {
  site: boolean;
  current: string;
  min: string | undefined;
  latest: string | undefined;
}): StartupDecision {
  if (input.site) return 'ok';
  if (isBelowMinVersion(input.current, input.min)) return 'update-required';
  if (isNewerVersion(input.latest, input.current)) return 'update-available';
  return 'ok';
}

/** Платформа приложения для ключей `minVersion.*` / `latestVersion.*`. */
export function appPlatform(): 'ios' | 'android' {
  return Capacitor.getPlatform() === 'android' ? 'android' : 'ios';
}

export async function decideStartup(logout: () => void): Promise<StartupDecision> {
  // На сайте обновлять нечего: там всегда свежая страница.
  if (!isStoreBuild()) return 'ok';

  // Принудительный выход по требованию сервера — до проверки версии: если
  // сессия испорчена, человеку всё равно входить заново.
  const epoch = getSessionEpoch();
  if (epoch !== undefined) {
    if (shouldForceLogout(epoch, readSeenEpoch())) logout();
    rememberEpoch(epoch);
  }

  const platform = appPlatform();
  const min = getMinVersion(platform);
  const latest = getLatestVersion(platform);
  if (!min && !latest) return 'ok';

  const current = await getAppVersion();
  return decideUpdate({ site: false, current, min, latest });
}
